// useBaseExpiredBadges.js

import { useEffect, useMemo, useState } from 'react';

import {
  getBaseBillingRecord,
  isBaseTrialAccessLocked,
} from 'services/billing/BaseBillingService';
import { getPlanByKey } from 'services/plans/PlanService';
import { getBaseKey } from 'services/bases/BaseReadService';

const getBaseBadgeSignature = (bases) => {
  const list = Array.isArray(bases) ? bases : [];
  return list
    .map((base) => {
      const key = getBaseKey(base);
      if (!key) return '';

      return [
        key,
        base?.planBypassesTrialLock === true ? '1' : '0',
        base?.trialExpired === true ? '1' : '0',
        String(base?.planKey || base?.planId || '').trim(),
        String(base?.trialEndsAt?.toMillis?.() || base?.trialEndsAt?.seconds || ''),
      ].join(':');
    })
    .filter(Boolean)
    .sort()
    .join('|');
};

/**
 * Resolves which bases should show an Expired badge in the switcher list.
 * Mirrors trial lock logic (paid billing bypasses stale trialExpired flags).
 */
const useBaseExpiredBadges = (bases = []) => {
  const baseSignature = useMemo(
    () => getBaseBadgeSignature(bases),
    [bases]
  );
  const [expiredByKey, setExpiredByKey] = useState(() => new Map());

  useEffect(() => {
    let isDisposed = false;
    const list = Array.isArray(bases) ? bases.filter(Boolean) : [];

    if (!list.length) {
      setExpiredByKey(new Map());
      return undefined;
    }

    Promise.all(list.map(async (base) => {
      const baseKey = getBaseKey(base);
      if (!baseKey) {
        return [null, false];
      }

      if (base?.planBypassesTrialLock === true) {
        return [baseKey, false];
      }

      let billingRecord = null;
      let plan = null;
      const planKey = String(base?.planKey || base?.planId || '').trim();

      try {
        billingRecord = await getBaseBillingRecord(baseKey);
      } catch (error) {
        console.warn('[useBaseExpiredBadges] billing lookup failed:', {
          baseKey,
          error,
        });
      }

      if (planKey) {
        try {
          plan = await getPlanByKey({ planKey });
        } catch (error) {
          console.warn('[useBaseExpiredBadges] plan lookup failed:', {
            planKey,
            error,
          });
        }
      }

      return [
        baseKey,
        isBaseTrialAccessLocked(base, billingRecord, new Date(), plan),
      ];
    }))
      .then((entries) => {
        if (isDisposed) return;

        const next = new Map();
        entries.forEach(([baseKey, isExpired]) => {
          if (baseKey) {
            next.set(baseKey, isExpired === true);
          }
        });
        setExpiredByKey(next);
      })
      .catch((error) => {
        if (isDisposed) return;
        console.warn('[useBaseExpiredBadges] failed to resolve badges:', error);
        setExpiredByKey(new Map());
      });

    return () => {
      isDisposed = true;
    };
  }, [baseSignature, bases]);

  return expiredByKey;
};

export default useBaseExpiredBadges;
